import { noop } from 'lodash';
import React, { Component } from 'react';
import { MFAVerificationForm } from '@zocdoc-frontend-common/components-mfa-verification-form';
import {
    FacebookLoginButton,
    GoogleLoginButton,
    AppleLoginButton,
} from '@zocdoc-frontend-common/components-sociallogin';
import {
    MetricsClickable,
    INTERACTION_TYPES,
    EVENT_INITIATORS,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { PATIENT_MODE, LOGIN_TYPE } from '../../constants';
import * as styled from './primitives.js';

const redirectUser = (suggestedRedirect) => {
    window.location.assign(suggestedRedirect);
};

/**
 * @param {string} appleLoginUrl
 * @param {string} facebookLoginUrl
 * @param {string} googleLoginUrl
 * @param {string} initialMode
 * @param {boolean} isInEmailFirstLoginAndSignup
 * @param {boolean} isMfaEnabled
 * @param {boolean} isUserLocked
 * @param {function(): void} onClickAppleLogin
 * @param {function(): void} onClickCreateAccount
 * @param {function(): void} onClickFacebookLogin
 * @param {function(): void} onClickGoogleLogin
 * @param {boolean} rememberMe
 * @param {string} returnUrl
 * @param {Functions_Shared_Fem_SendMetric} sendMetric
 * @param {function(): void} showAppleLogin
 * @param {function(): void} showFacebookLogin
 * @param {function(): void} showGoogleLogin
 * @param {function(): void} showRememberMe
 * @param {number} socialLoginValidationError
 * @param {string} suggestedRedirect
 * @param {function(): void} trackClickCreateAccount
 * @param {boolean} __forceShowPasswordForm
 * @returns {JSX.Element}
 */
export class SignInFormPatientProviderModeView extends Component {
    static defaultProps = {
        facebookLoginUrl: '',
        googleLoginUrl: '',
        appleLoginUrl: '',
        isInEmailFirstLoginAndSignup: false,
        initialMode: PATIENT_MODE,
        isMfaEnabled: false,
        isUserLocked: false,
        onClickCreateAccount: noop,
        onClickFacebookLogin: noop,
        onClickGoogleLogin: noop,
        onClickAppleLogin: noop,
        rememberMe: true,
        returnUrl: '',
        showGoogleLogin: false,
        showFacebookLogin: false,
        showAppleLogin: false,
        showRememberMe: false,
        suggestedRedirect: '',
        trackClickCreateAccount: noop,
        __forceShowPasswordForm: false,
    };

    state = {
        // initialMode prop is seed data for isPatientMode state
        isPatientMode: this.props.initialMode === PATIENT_MODE,
        showPasswordForm: this.props.__forceShowPasswordForm,
        providedEmail: null,
    };

    componentDidUpdate(prevProps) {
        const { isUserLocked, suggestedRedirect } = this.props;

        if (isUserLocked && prevProps.isUserLocked !== isUserLocked) {
            window.location.assign(suggestedRedirect);
        }
    }

    maybeRenderPatientModeButtons() {
        return this.state.isPatientMode ? (
            <div data-test="patient-mode-login-buttons">
                {this.maybeRenderGoogleLoginButton()}
                {this.maybeRenderFacebookLoginButton()}
                {this.maybeRenderAppleLoginButton()}
                {this.renderSignUpButton()}
            </div>
        ) : null;
    }

    maybeRenderGoogleLoginButton() {
        const { showGoogleLogin } = this.props;

        return showGoogleLogin ? (
            <styled.SocialLoginButtonWrapper>
                <GoogleLoginButton
                    data-test="google-login-button"
                    onClick={this.handleClickGoogleLogin}
                />
            </styled.SocialLoginButtonWrapper>
        ) : null;
    }

    // we won't enable this until ios13 ships
    maybeRenderAppleLoginButton() {
        const { showAppleLogin } = this.props;

        return showAppleLogin ? (
            <styled.SocialLoginButtonWrapper>
                <AppleLoginButton
                    data-test="apple-login-button"
                    onClick={this.handleClickAppleLogin}
                />
            </styled.SocialLoginButtonWrapper>
        ) : null;
    }

    maybeRenderFacebookLoginButton() {
        const { showFacebookLogin } = this.props;

        return showFacebookLogin ? (
            <styled.SocialLoginButtonWrapper>
                <FacebookLoginButton
                    data-test="facebook-login-button"
                    onClick={this.handleClickFacebookLogin}
                />
            </styled.SocialLoginButtonWrapper>
        ) : null;
    }

    renderSignUpButton() {
        return (
            <styled.SignupButton
                data-test="create-account-button"
                fluid
                onClick={this.handleClickCreateAccount}
            >
                Sign up with email
            </styled.SignupButton>
        );
    }

    renderSignInForm() {
        const {
            initialMode,
            isInEmailFirstLoginAndSignup,
            isMfaEnabled,
            onClickCreateAccount,
            onClickFacebookLogin,
            onClickGoogleLogin,
            onClickAppleLogin,
            showFacebookLogin,
            showGoogleLogin,
            showAppleLogin,
            showRememberMe,
            socialLoginValidationError,
            ...props
        } = this.props;

        const { showPasswordForm, providedEmail } = this.state;

        if (isMfaEnabled) {
            return <MFAVerificationForm />;
        } else {
            return (
                <>
                    {this.state.isPatientMode &&
                    isInEmailFirstLoginAndSignup &&
                    !showPasswordForm ? (
                        <styled.StyledSignInWithEmail
                            onSubmitEmail={this.onSubmitEmail}
                            showFacebookLogin={showFacebookLogin}
                            socialLoginValidationError={
                                socialLoginValidationError
                            }
                        />
                    ) : (
                        <styled.StyledSignInForm
                            {...props}
                            data-test="styled-sign-in-form"
                            isPatientMode={this.state.isPatientMode}
                            isInMultiStepSignIn={
                                isInEmailFirstLoginAndSignup &&
                                this.state.isPatientMode
                            }
                            defaultEmailValue={providedEmail}
                            showFacebookLogin={showFacebookLogin}
                            showRememberMe={showRememberMe}
                            socialLoginValidationError={
                                socialLoginValidationError
                            }
                            $isInEmailFirstLoginAndSignup={
                                isInEmailFirstLoginAndSignup &&
                                this.state.isPatientMode
                            }
                            showFieldLabel={this.state.isPatientMode}
                            onEditEmailClick={this.onEditEmailClick}
                        />
                    )}
                    {this.state.isPatientMode &&
                        isInEmailFirstLoginAndSignup &&
                        showPasswordForm && (
                            <MetricsClickable
                                action={'Click Forgot your password'}
                                element={'ForgotYourPasswordLink'}
                            >
                                <styled.ForgotPasswordWrapper>
                                    <styled.ForgotPasswordLink
                                        href={'/auth/ForgotPassword'}
                                    >
                                        Forgot your password?
                                    </styled.ForgotPasswordLink>
                                </styled.ForgotPasswordWrapper>
                            </MetricsClickable>
                        )}
                </>
            );
        }
    }

    render() {
        const { isMfaEnabled } = this.props;

        return (
            <div>
                {this.maybeRenderPatientModeButtons()}
                <styled.StandardLoginWrapper>
                    {this.state.isPatientMode ? (
                        <Divider />
                    ) : (
                        !isMfaEnabled && (
                            <styled.StandardLoginTitle data-test="provider-login-title">
                                Log in with email
                            </styled.StandardLoginTitle>
                        )
                    )}
                    {this.renderSignInForm()}
                </styled.StandardLoginWrapper>
            </div>
        );
    }

    getJsonStateParamForSocialLogin = () => {
        const { rememberMe, returnUrl, showRememberMe } = this.props;

        const { isPatientMode } = this.state;

        const stateQueryParam = {
            remember_me: rememberMe && isPatientMode && showRememberMe,
            return_url: returnUrl,
        };

        return encodeURIComponent(JSON.stringify(stateQueryParam));
    };

    onSubmitEmail = (email) => {
        const { sendMetric } = this.props;

        sendMetric({
            action: 'Click Forgot your password',
            interactionType: INTERACTION_TYPES.CLICK,
            initiator: EVENT_INITIATORS.USER,
            element: 'ForgotYourPasswordLink',
        });

        this.setState({ showPasswordForm: true, providedEmail: email });
    };

    handleClickSocialLogin = (loginType, event) => {
        event.preventDefault();

        const {
            facebookLoginUrl,
            googleLoginUrl,
            appleLoginUrl,
            onClickFacebookLogin,
            onClickGoogleLogin,
            onClickAppleLogin,
            returnUrl,
            showRememberMe,
        } = this.props;

        let redirectUrl;

        const stateQueryParam = showRememberMe
            ? this.getJsonStateParamForSocialLogin()
            : returnUrl;

        switch (loginType) {
            case LOGIN_TYPE.facebook:
                onClickFacebookLogin(event);
                redirectUrl = `${facebookLoginUrl}&state=${stateQueryParam}`;
                break;
            case LOGIN_TYPE.apple:
                onClickAppleLogin(event);
                redirectUrl = `${appleLoginUrl}&state=${stateQueryParam}`;
                break;
            case LOGIN_TYPE.google:
                onClickGoogleLogin(event);
                redirectUrl = `${googleLoginUrl}&state=${stateQueryParam}`;
                break;
            default:
                break;
        }

        redirectUser(redirectUrl);
    };

    handleClickFacebookLogin = (event) =>
        this.handleClickSocialLogin(LOGIN_TYPE.facebook, event);
    handleClickGoogleLogin = (event) =>
        this.handleClickSocialLogin(LOGIN_TYPE.google, event);
    handleClickAppleLogin = (event) =>
        this.handleClickSocialLogin(LOGIN_TYPE.apple, event);

    handleClickCreateAccount = (event) => {
        event.preventDefault();
        const { onClickCreateAccount, trackClickCreateAccount } = this.props;
        trackClickCreateAccount(event);
        onClickCreateAccount();
    };

    onEditEmailClick = () => {
        this.setState({
            providedEmail: null,
            showPasswordForm: false,
        });
    };
}

const Divider = () => {
    return (
        <styled.DividerWrapper data-test={'standard-login-divider-text'}>
            <styled.DividerHorizontalLine />
            <styled.DividerText>or</styled.DividerText>
            <styled.DividerHorizontalLine />
        </styled.DividerWrapper>
    );
};
